import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Long-form Thai guide: spouse-visa document checklist and certification order. */
const TITLE = "เอกสารสำหรับวีซ่าคู่สมรส — เช็กลิสต์และลำดับการรับรอง";
const DESC =
  "รายการเอกสารวีซ่าคู่สมรสที่ต้องเตรียม ลำดับการรับรองจากทนาย Notary ไปกรมการกงสุลและสถานทูต พร้อมสาเหตุที่เอกสารถูกตีกลับบ่อยที่สุด";
const URL = "/blog/spouse-visa-document-checklist";
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "เอกสารวีซ่าคู่สมรสต้องแปลทุกฉบับหรือไม่",
    answer:
      "เอกสารภาษาไทยที่ยื่นต่อหน่วยงานต่างประเทศต้องมีคำแปลเป็นภาษาที่หน่วยงานนั้นกำหนดเกือบทุกกรณี รวมถึงทะเบียนสมรส ทะเบียนบ้าน สูติบัตรของบุตร และหนังสือรับรองสถานะ ส่วนรูปแบบคำแปลและผู้แปลที่ยอมรับ ให้ยึดตามประกาศของหน่วยงานปลายทาง",
  },
  {
    question: "ต้องรับรองที่กรมการกงสุลก่อนไปสถานทูตหรือไม่",
    answer:
      "โดยทั่วไปใช่ ลำดับที่ใช้กันคือรับรองต้นทาง (นายทะเบียนหรือทนายผู้ทำคำรับรองลายมือชื่อ) แล้วยื่นนิติกรณ์ที่กรมการกงสุล จากนั้นจึงนำไปรับรองที่สถานทูตของประเทศปลายทางเฉพาะกรณีที่สถานทูตนั้นกำหนด การข้ามลำดับทำให้ต้องเริ่มใหม่",
  },
  {
    question: "หลักฐานความสัมพันธ์ควรมีอะไร",
    answer:
      "หน่วยงานตรวจคนเข้าเมืองส่วนใหญ่พิจารณาความต่อเนื่องของความสัมพันธ์ เอกสารที่ใช้บ่อยคือทะเบียนสมรส ภาพถ่ายที่มีบริบทและวันที่ ประวัติการเดินทางร่วมกัน หลักฐานการติดต่อสื่อสาร และหลักฐานการอยู่อาศัยหรือค่าใช้จ่ายร่วมกัน ควรจัดเรียงตามลำดับเวลาเพื่อให้ตรวจง่าย",
  },
  {
    question: "หนังสือให้ความยินยอมของบิดามารดาจำเป็นเมื่อไร",
    answer:
      "จำเป็นเมื่อมีบุตรผู้เยาว์เดินทางไปด้วยและมีผู้ปกครองไม่ได้เดินทางพร้อมกัน หนังสือยินยอมต้องลงนามต่อหน้าทนายผู้ทำคำรับรองลายมือชื่อ และมักต้องผ่านนิติกรณ์ก่อนใช้ในต่างประเทศ",
  },
  {
    question: "เอกสารต้องคัดใหม่ภายในกี่เดือน",
    answer:
      "หน่วยงานปลายทางหลายแห่งกำหนดให้เอกสารทะเบียนราษฎรออกใหม่ภายใน 3-6 เดือนก่อนวันยื่น จึงควรตรวจเงื่อนไขของหน่วยงานนั้นก่อนคัดเอกสาร เพื่อไม่ให้เอกสารหมดกรอบเวลาระหว่างรอขั้นตอนรับรอง",
  },
  {
    question: "ควรเริ่มเตรียมเอกสารล่วงหน้านานเท่าไร",
    answer:
      "ควรไล่ย้อนจากวันนัดยื่นหรือวันเดินทาง แล้วเผื่อเวลาให้ทุกชั้นการรับรอง โดยขั้นตอนที่ควบคุมเวลาได้น้อยที่สุดคือคิวนัดหมายของสถานทูต ไม่ใช่เวลาจัดทำคำแปล",
  },
];

export const Route = createFileRoute("/blog/spouse-visa-document-checklist")({
  component: SpouseVisaChecklist,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("spouse-visa-document-checklist", "th") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "เอกสารวีซ่าคู่สมรส, วีซ่าติดตามคู่สมรส, แปลทะเบียนสมรส, รับรองเอกสารวีซ่า, หนังสือยินยอมให้บุตรเดินทาง",
        },
      ],
      links: canonicalAndHreflang(URL),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บทความ", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("spouse-visa-document-checklist"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Visa & Immigration",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const CHECKLIST: Array<{ group: string; items: string[] }> = [
  {
    group: "เอกสารสถานะบุคคลและการสมรส",
    items: [
      "ทะเบียนสมรส (คร.2) หรือใบสำคัญการสมรส (คร.3) คัดรับรองใหม่",
      "ทะเบียนบ้านของคู่สมรสฝ่ายไทย",
      "บัตรประชาชนและหนังสือเดินทางของทั้งสองฝ่าย",
      "หนังสือรับรองสถานะ เช่น หนังสือรับรองความเป็นโสดก่อนสมรส (กรณีที่ปลายทางขอย้อนหลัง)",
      "ทะเบียนหย่าหรือใบสำคัญการหย่า และมรณบัตรของคู่สมรสเดิม หากเคยสมรสมาก่อน",
    ],
  },
  {
    group: "เอกสารเกี่ยวกับบุตร (ถ้ามี)",
    items: [
      "สูติบัตรของบุตรทุกคนที่เกี่ยวข้อง",
      "หนังสือรับรองการปกครองบุตร หรือคำสั่งศาลเรื่องอำนาจปกครอง",
      "หนังสือให้ความยินยอมให้บุตรเดินทางไปต่างประเทศ ลงนามต่อหน้าทนายผู้ทำคำรับรองลายมือชื่อ",
    ],
  },
  {
    group: "หลักฐานความสัมพันธ์",
    items: [
      "ภาพถ่ายร่วมกันที่ระบุช่วงเวลาและสถานที่ได้",
      "ประวัติการเดินทางเข้าออกและตราประทับหนังสือเดินทาง",
      "หลักฐานการติดต่อสื่อสารต่อเนื่อง",
      "หลักฐานที่อยู่ร่วมกัน หรือค่าใช้จ่ายร่วมกัน",
      "คำให้การรับรองความสัมพันธ์ (statutory declaration) หากปลายทางกำหนด",
    ],
  },
  {
    group: "เอกสารการเงินและที่อยู่",
    items: [
      "หนังสือรับรองการทำงานและสลิปเงินเดือนของผู้สนับสนุน",
      "หนังสือรับรองฐานะการเงินหรือรายการเดินบัญชี",
      "สัญญาเช่าหรือหลักฐานกรรมสิทธิ์ที่อยู่อาศัยปลายทาง",
    ],
  },
];

function SpouseVisaChecklist() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/" className="text-muted-foreground hover:underline">หน้าแรก</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:underline">บทความ</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span>เอกสารวีซ่าคู่สมรส</span>
      </nav>

      <h1>เอกสารสำหรับวีซ่าคู่สมรส — เช็กลิสต์และลำดับการรับรอง</h1>
      <BlogHero slug="spouse-visa-document-checklist" locale="th" />
      <p className="speakable-summary lead answer-summary">
        เอกสารวีซ่าคู่สมรสแบ่งเป็นสี่กลุ่ม คือ เอกสารสถานะบุคคลและการสมรส เอกสารเกี่ยวกับบุตร
        หลักฐานความสัมพันธ์ และเอกสารการเงิน/ที่อยู่ ทุกฉบับที่ออกในประเทศไทยต้องคัดใหม่ในกรอบเวลาที่ปลายทางกำหนด
        มีคำแปลตามรูปแบบที่หน่วยงานผู้รับยอมรับ และผ่านลำดับการรับรอง
        ทนายผู้ทำคำรับรองลายมือชื่อหรือหน่วยงานผู้ออกเอกสาร แล้วจึงกรมการกงสุล และสถานทูตปลายทางเมื่อจำเป็น
      </p>

      <h2>1. เช็กลิสต์เอกสาร</h2>
      {CHECKLIST.map((g) => (
        <div key={g.group}>
          <h3>{g.group}</h3>
          <ul>
            {g.items.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      ))}

      <h2>2. ลำดับการรับรองที่ถูกต้อง</h2>
      <ol>
        <li>
          <strong>คัดเอกสารต้นทาง</strong> เอกสารทะเบียนราษฎรคัดรับรองจากสำนักงานเขตหรือที่ว่าการอำเภอ
          เอกสารเอกชนและหนังสือยินยอมลงนามต่อหน้าทนายผู้ทำคำรับรองลายมือชื่อ
        </li>
        <li>
          <strong>จัดทำคำแปล</strong> ตามภาษาและรูปแบบที่หน่วยงานปลายทางกำหนด
          ตรวจการสะกดชื่อให้ตรงหนังสือเดินทางทุกฉบับ
        </li>
        <li>
          <strong>ยื่นนิติกรณ์กรมการกงสุล</strong> พร้อมต้นฉบับ คำแปล และแบบคำร้อง
        </li>
        <li>
          <strong>รับรองสถานทูตปลายทาง</strong> เฉพาะประเทศที่กำหนด และตามจำนวนชุดที่ระบุ
        </li>
        <li>
          <strong>จัดชุดยื่น</strong> ทำบัญชีรายการเอกสาร เรียงตามลำดับที่แบบฟอร์มของหน่วยงานกำหนด
        </li>
      </ol>
      <p className="text-sm">
        แหล่งอ้างอิง:{" "}
        <a href="https://consular.mfa.go.th/th/publicservice/legalization" target="_blank" rel="noreferrer">
          กรมการกงสุล กระทรวงการต่างประเทศ — งานนิติกรณ์
        </a>{" "}
        ·{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          สภาทนายความในพระบรมราชูปถัมภ์ (ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร)
        </a>{" "}
        ·{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">
          NAATI (กรณีเอกสารยื่นในออสเตรเลีย)
        </a>
      </p>

      <h2>3. สาเหตุที่เอกสารถูกตีกลับบ่อยที่สุด</h2>
      <ul>
        <li>สะกดชื่อในคำแปลไม่ตรงกับหนังสือเดินทาง หรือไม่ตรงกันระหว่างเอกสารในชุดเดียวกัน</li>
        <li>ทะเบียนบ้านหรือทะเบียนสมรสคัดไว้นานเกินกรอบเวลาที่ปลายทางกำหนด</li>
        <li>หนังสือยินยอมให้บุตรเดินทางไม่ได้ลงนามต่อหน้าทนาย หรือไม่ผ่านนิติกรณ์</li>
        <li>หลักฐานความสัมพันธ์กระจัดกระจาย ไม่มีลำดับเวลา ทำให้เจ้าหน้าที่ตรวจไม่ต่อเนื่อง</li>
        <li>ยื่นสถานทูตก่อนผ่านนิติกรณ์ ทำให้ต้องเริ่มลำดับใหม่</li>
        <li>จำนวนชุดไม่พอ เพราะหน่วยงานเก็บต้นฉบับที่รับรองแล้วไว้</li>
      </ul>

      <h2>4. การวางแผนเวลา</h2>
      <p>
        เริ่มจากวันนัดยื่นหรือวันเดินทาง แล้วไล่ย้อนกลับ ให้เวลาที่ยืดหยุ่นน้อยที่สุดคือคิวนัดหมายสถานทูต
        จากนั้นค่อยกำหนดวันคัดเอกสารต้นทาง เพื่อไม่ให้เอกสารหมดกรอบเวลาระหว่างรอ
        ในเคสที่มีบุตรผู้เยาว์หรือมีการสมรสก่อนหน้า ควรเผื่อเวลาเพิ่มสำหรับเอกสารย้อนหลัง
        เช่น ทะเบียนหย่าหรือคำสั่งศาล ซึ่งบางครั้งต้องขอคัดจากหน่วยงานต่างจังหวัด
      </p>

      <h2>5. เราช่วยอะไรได้</h2>
      <p>
        เราให้คำปรึกษาก่อนเริ่มงานทุกเคส ตรวจเอกสารทั้งชุดก่อนยื่น จัดลำดับตามประเทศปลายทาง
        รับรองลายมือชื่อโดยทนายผู้ได้รับใบอนุญาต จัดคำแปล ยื่นนิติกรณ์ และประสานสถานทูตจนจบกระบวนการ
        อ่านเพิ่มที่ <Link to="/blog/apostille-vs-legalization-by-country">Apostille กับ Legalization ต่างกันอย่างไร</Link>{" "}
        และ <Link to="/blog/naati-vs-court-registered-translator">การเลือกผู้แปลให้ตรงหน่วยงาน</Link> ·{" "}
        <Link to="/services">บริการทั้งหมด</Link> · <Link to="/contact">ติดต่อเรา</Link> หรือโทร{" "}
        <a href="tel:+66948958999">094-895-8999</a>
      </p>

      <section className="not-prose mt-12 border-t pt-8">
        <h2 className="text-2xl font-semibold mb-4">คำถามที่พบบ่อย</h2>
        <div className="space-y-4">
          {FAQ.map((f) => (
            <details key={f.question} className="rounded-lg border p-4">
              <summary className="font-medium cursor-pointer">{f.question}</summary>
              <p className="mt-2 text-muted-foreground faq-answer">{f.answer}</p>
            </details>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          ตรวจทานเนื้อหาโดยทีมทนายผู้ทำคำรับรองลายมือชื่อและเอกสารของสำนักงาน · ตรวจทานล่าสุด 10 สิงหาคม 2569
          · เนื้อหานี้เป็นข้อมูลทั่วไปเกี่ยวกับขั้นตอน ไม่ใช่คำแนะนำทางกฎหมายเฉพาะกรณี
        </p>
      </section>
    </article>
  );
}
